import { useEffect, useMemo, useState } from "react";
import {
  Box3,
  CanvasTexture,
  Color,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector3,
  MeshStandardMaterial,
} from "three";
import type { Group, Object3D } from "three";
import { createGltfLoader } from "../../loaders/loaders";

const textureCache = new Map<string, Texture | null>();

/**
 * Carga una textura desde /public con fallback silencioso:
 * si el archivo no existe (404 u otro error), devuelve null y el
 * llamador usa color + roughness. Nunca deja una textura rota.
 */
export function useOptionalTexture(
  url: string,
  repeat?: [number, number],
): Texture | null {
  const [texture, setTexture] = useState<Texture | null>(() => textureCache.get(url) ?? null);

  useEffect(() => {
    if (textureCache.has(url)) {
      setTexture(textureCache.get(url) ?? null);
      return;
    }
    let cancelled = false;
    new TextureLoader().load(
      url,
      (tex) => {
        tex.colorSpace = SRGBColorSpace;
        tex.wrapS = RepeatWrapping;
        tex.wrapT = RepeatWrapping;
        if (repeat) tex.repeat.set(repeat[0], repeat[1]);
        textureCache.set(url, tex);
        if (!cancelled) setTexture(tex);
      },
      undefined,
      () => {
        textureCache.set(url, null);
        if (!cancelled) setTexture(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [url, repeat]);

  return texture;
}

export interface OptionalGltf {
  scene: Group | null;
  clips: import("three").AnimationClip[];
  /** true mientras se intenta la carga; false cuando hay resultado (éxito o fallo). */
  loading: boolean;
}

/**
 * Corrige materiales GLB que renderizan como silueta negra: metalness alto
 * sin envMap, texturas sin sRGB. Se aplica una sola vez por escena cargada.
 */
export function fixGltfMaterials(root: Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as import("three").Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of materials) {
      const std = mat as import("three").MeshStandardMaterial;
      if (std.map) std.map.colorSpace = SRGBColorSpace;
      if (typeof std.metalness === "number") std.metalness = Math.min(std.metalness, 0.4);
      if (typeof std.roughness === "number") std.roughness = Math.max(std.roughness, 0.4);
      if ("envMapIntensity" in std) std.envMapIntensity = 1.1;
      std.needsUpdate = true;
    }
  });
}

/**
 * Normaliza la escala de un objeto GLB a una dimensión real objetivo:
 * mide el bbox y escala uniformemente hasta que el eje indicado
 * ("x" | "y" | "z" | "max") mida `targetMeters`. Devuelve el factor aplicado.
 */
export function normalizeScale(
  root: Object3D,
  targetMeters: number,
  axis: "x" | "y" | "z" | "max" = "max",
): number {
  const bbox = new Box3().setFromObject(root);
  const size = bbox.getSize(new Vector3());
  const current = axis === "max" ? Math.max(size.x, size.y, size.z) : size[axis];
  if (!Number.isFinite(current) || current <= 0) return 1;
  const factor = targetMeters / current;
  if (Math.abs(factor - 1) < 0.02) return 1;
  root.scale.multiplyScalar(factor);
  return factor;
}

export interface NormalizeSpec {
  target: number;
  axis?: "x" | "y" | "z" | "max";
}

/**
 * Intenta cargar un GLB; si no existe devuelve scene=null sin lanzar,
 * para que el llamador instancie un placeholder procedural. Con
 * `normalize` ajusta la escala a una dimensión real (metros).
 */
export function useOptionalGLTF(url: string, normalize?: NormalizeSpec): OptionalGltf {
  const [result, setResult] = useState<OptionalGltf>({ scene: null, clips: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    setResult({ scene: null, clips: [], loading: true });
    createGltfLoader().load(
      url,
      (gltf) => {
        fixGltfMaterials(gltf.scene);
        if (normalize) normalizeScale(gltf.scene, normalize.target, normalize.axis ?? "max");
        if (!cancelled) setResult({ scene: gltf.scene, clips: gltf.animations, loading: false });
      },
      undefined,
      () => {
        if (!cancelled) setResult({ scene: null, clips: [], loading: false });
      },
    );
    return () => {
      cancelled = true;
    };
  // `normalize` es una constante por llamador; solo la URL re-dispara la carga
  }, [url]);

  return result;
}

interface StandardParams {
  color: string;
  roughness?: number;
  metalness?: number;
  map?: Texture | null;
}

/** Material PBR con textura opcional (fallback a color plano). */
export function useStandardMaterial(params: StandardParams): MeshStandardMaterial {
  const { color, roughness = 0.85, metalness = 0, map = null } = params;
  return useMemo(() => {
    const mat = new MeshStandardMaterial({
      color: new Color(color),
      roughness,
      metalness,
    });
    if (map) mat.map = map;
    return mat;
  }, [color, roughness, metalness, map]);
}

/**
 * Textura procedural de malla de cerramiento (chain-link) con canal alpha,
 * generada en canvas: sin dependencia de archivos externos.
 */
export function useChainLinkTexture(): CanvasTexture {
  return useMemo(() => {
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, size, size);
    ctx.strokeStyle = "#8f979c";
    ctx.lineWidth = 3;
    const step = 16;
    for (let i = -size; i < size * 2; i += step) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + size, size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(i + size, 0);
      ctx.lineTo(i, size);
      ctx.stroke();
    }
    const tex = new CanvasTexture(canvas);
    tex.wrapS = RepeatWrapping;
    tex.wrapT = RepeatWrapping;
    return tex;
  }, []);
}
